import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyApi, type PropertyData } from '../services';
import { useToast } from '../contexts/ToastContext';
import './PropertiesPage.css';
import DonutChart from '../components/DonutChart';
import PolygonUp from '../components/PolygonUp';
import PolygonDown from '../components/PolygonDown';

const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);

  // Refs for map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  // Helper function to normalize property data
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    return {
      ...prop,
      kaufpreis:
        prop.kaufpreis !== undefined && prop.kaufpreis !== null
          ? typeof prop.kaufpreis === 'string'
            ? parseFloat(prop.kaufpreis)
            : prop.kaufpreis
          : undefined,
      currentValue:
        prop.currentValue !== undefined && prop.currentValue !== null
          ? typeof prop.currentValue === 'string'
            ? parseFloat(prop.currentValue)
            : prop.currentValue
          : undefined,
      gain: typeof prop.gain === 'string' ? parseFloat(prop.gain) : prop.gain,
      gainPercentage:
        typeof prop.gainPercentage === 'string'
          ? parseFloat(prop.gainPercentage)
          : prop.gainPercentage,
      latitude:
        prop.latitude !== undefined && prop.latitude !== null
          ? typeof prop.latitude === 'string'
            ? parseFloat(prop.latitude)
            : prop.latitude
          : undefined,
      longitude:
        prop.longitude !== undefined && prop.longitude !== null
          ? typeof prop.longitude === 'string'
            ? parseFloat(prop.longitude)
            : prop.longitude
          : undefined,
    };
  };

  const fetchChartData = async () => {
    try {
      setLoadingChart(true);
      const data = await propertyApi.getCityDistribution();
      setChartData(data);
    } catch (error: any) {
      console.error('Failed to load chart data:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const data = await propertyApi.getAll();
        const normalizedData = data.map(normalizeProperty);
        setProperties(normalizedData);
      } catch (error: any) {
        showError(error.message || 'Failed to load properties');
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [properties.length]); // Refresh when property count changes

  // Function to initialize map
  const initializeMap = () => {
    if (!mapRef.current) return false;

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Map) return false;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new googleMaps.maps.Map(mapRef.current, {
        center: { lat: 47.5162, lng: 14.5501 }, // Default to Austria
        zoom: 7,
        disableDefaultUI: true,
      });
      setIsMapInitialized(true);
    }
    return true;
  };

  // Initialize map on mount/load
  useEffect(() => {
    let isMounted = true;
    const initTimeout = setTimeout(() => {
      if (!isMounted) return;

      if (!initializeMap()) {
        let retryCount = 0;
        const maxRetries = 100;
        const retryInterval = setInterval(() => {
          if (!isMounted) {
            clearInterval(retryInterval);
            return;
          }
          retryCount++;
          if (initializeMap() || retryCount >= maxRetries) {
            clearInterval(retryInterval);
          }
        }, 100);
      }
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
    };
  }, []);

  // Update markers and bounds when properties change
  useEffect(() => {
    if (!isMapInitialized || !mapInstanceRef.current) return;

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const bounds = new googleMaps.maps.LatLngBounds();
    let hasMarkers = false;

    properties.forEach((property) => {
      if (property.latitude && property.longitude) {
        const position = { lat: property.latitude, lng: property.longitude };
        const marker = new googleMaps.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: property.address,
        });

        // Add click listener to navigate to property page
        marker.addListener('click', () => {
          navigate(`/properties/${property.id}`);
        });

        markersRef.current.push(marker);
        bounds.extend(position);
        hasMarkers = true;
      }
    });

    if (hasMarkers) {
      mapInstanceRef.current.fitBounds(bounds);
      // Don't zoom in too much for a single marker
      const listener = googleMaps.maps.event.addListener(
        mapInstanceRef.current,
        'idle',
        () => {
          if (
            mapInstanceRef.current &&
            mapInstanceRef.current.getZoom()! > 15
          ) {
            mapInstanceRef.current.setZoom(15);
          }
          googleMaps.maps.event.removeListener(listener);
        },
      );
    }
  }, [properties, isMapInitialized, navigate]);

  const handlePropertyClick = (id: string) => {
    navigate(`/properties/${id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="properties-page">
      <main className="properties-content">
        <div className="properties-section-header">
          <h2>
            Meine Immobilien
            <span className="property-count">({properties.length})</span>
          </h2>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Lade Immobilien...
          </div>
        ) : properties.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>
              Noch keine Immobilien vorhanden. Klicken Sie auf "Neue Immobilie
              hinzufügen", um loszulegen!
            </p>
          </div>
        ) : (
          <>
            <div className="properties-main-content">
              <div className="properties-list">
                {properties.map((property) => (
                  <div
                    key={property.id}
                    className="property-item clickable"
                    onClick={() => handlePropertyClick(property.id)}
                  >
                    <div className="property-info">
                      <div className="property-address">
                        {[
                          [property.streetName, property.houseNumber]
                            .filter(Boolean)
                            .join(' '),
                          property.city,
                          property.country,
                        ]
                          .filter(Boolean)
                          .join(' • ') ||
                          property.address ||
                          'No address'}
                      </div>
                      <div className="property-details">
                        <span className="property-value">
                          {formatCurrency(property.currentValue || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="property-meta">
                      <div
                        className={`property-gain-container ${property.gain >= 0 ? 'stat-positive' : 'stat-negative'}`}
                      >
                        <div className="gain-icon">
                          {property.gain >= 0 ? <PolygonUp /> : <PolygonDown />}
                        </div>
                        <div className="gain-percentage">
                          {property.gainPercentage.toFixed(1)}%
                        </div>
                        <div className="gain-currency">
                          {property.gain > 0 ? '+' : ''}
                          {formatCurrency(property.gain)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="properties-stats">
                <div className="properties-pie-chart">
                  <h4 className="properties-title">Stadtverteilung</h4>
                  <div className="properties-pie-chart-content">
                    {loadingChart ? (
                      <div
                        style={{
                          height: 246,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        Loading chart...
                      </div>
                    ) : (
                      <DonutChart data={chartData} />
                    )}
                  </div>
                </div>
                <div className="properties-map">
                  <h4 className="properties-title">Standorte</h4>
                  <div className="properties-map-content" ref={mapRef}></div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PropertiesPage;
